import React, {lazy, type ReactNode, Suspense, useEffect, useRef, useSyncExternalStore} from 'react'
import {getChatConnectionSnapshot, isLiveChatConnection, subscribeChatConnection} from '@/Library/Chat/chatShell'
import {
    getChatPanelSnapshot,
    minimizeChatPanel,
    rememberChatChannel,
    subscribeChatPanel,
    syncChatPanelOrganization,
} from '@/Library/Chat/chatPanelShell'
import ChatPanelHeader from '@/Library/Chat/ChatPanelHeader'
import {chatConnectionNotice} from '@/Library/Chat/chatConnectionNotice'
import {openChatPage} from '@/Library/Chat/chatPageShell'
import {dockedPanelClass} from '@/Layouts/dock'

// The one import of the module that holds every `stream-chat-react` import and the SDK's stylesheet: a page
// fetches that chunk only when the panel first opens on a live connection.
const ChatPanel = lazy(() => import('./chatPanel'))

const panelClass = `chat-panel ${dockedPanelClass}`

/** The header and the connection notice every state but `connected` shows; also the lazy panel's loading fallback. */
const status = (connection: Parameters<typeof chatConnectionNotice>[0]): ReactNode => (
    <>
        <ChatPanelHeader title="Chat" onClose={minimizeChatPanel}/>
        {chatConnectionNotice(connection)}
    </>
)

/**
 * The docked chat panel (ENG-4739), mounted beside the staff assistant host outside the page, so a minimized
 * panel keeps its channel across Inertia visits. It reads the page's connection straight from `chatShell`, since
 * it sits outside the layout's provider, and renders nothing until the connection is on and the panel has been
 * opened once. Minimize hides it in place; a connection to another organization closes it and forgets the channel.
 */
const ChatHost = (): ReactNode => {
    const connection = useSyncExternalStore(subscribeChatConnection, getChatConnectionSnapshot, getChatConnectionSnapshot)
    const panel = useSyncExternalStore(subscribeChatPanel, getChatPanelSnapshot, getChatPanelSnapshot)
    const shownRef = useRef(false)
    const asideRef = useRef<HTMLElement>(null)

    useEffect(() => {
        syncChatPanelOrganization(connection.organizationId)
    }, [connection.organizationId])

    useEffect(() => {
        if (!panel.open) {
            if (asideRef.current?.contains(document.activeElement)) {
                document.querySelector<HTMLElement>('[data-testid="chat-toggle"]')?.focus({preventScroll: true})
            }

            return
        }

        asideRef.current?.focus({preventScroll: true})

        const onKey = (event: KeyboardEvent): void => {
            // Escape on the panel's own chrome minimizes; inside the SDK's UI it belongs to the SDK (menus, pickers).
            const target = event.target instanceof Element ? event.target : null

            if (event.key === 'Escape' && !event.defaultPrevented && asideRef.current?.contains(target) && target?.closest('.str-chat') === null) {
                event.preventDefault()
                minimizeChatPanel()
            }
        }

        window.addEventListener('keydown', onKey)

        return () => {
            window.removeEventListener('keydown', onKey)
        }
    }, [panel.open])

    if (panel.open) {
        shownRef.current = true
    }

    if (connection.status === 'off' || !shownRef.current) {
        return null
    }

    return (
        <aside
            ref={asideRef}
            id="chat-panel"
            data-testid="chat-panel"
            data-status={connection.status}
            aria-label="Chat"
            tabIndex={-1}
            hidden={!panel.open}
            className={panelClass}
        >
            {isLiveChatConnection(connection) ? (
                <Suspense fallback={status({status: 'connecting', organizationId: connection.organizationId})}>
                    <ChatPanel
                        client={connection.client}
                        organizationId={connection.organizationId}
                        userId={connection.userId}
                        open={panel.open}
                        channelId={panel.channelId}
                        onChannelChange={rememberChatChannel}
                        onExpand={openChatPage}
                        onClose={minimizeChatPanel}
                    />
                </Suspense>
            ) : status(connection)}
        </aside>
    )
}

export default ChatHost
